import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useLocale } from '@/i18n/LocaleContext';
import { SectionHeading } from './SectionHeading';

const CHAPTERS = [
  {
    key: 'nightMarket',
    image: '/assets/03-lore-scenes/02-night-market-flat.png',
    cutout: '/assets/01-mascot/cutouts/peipei-tea-flat.png',
    frame: 'hero-frame',
  },
  {
    key: 'yellowRain',
    image: '/assets/03-lore-scenes/03-yellow-rain-flat.png',
    cutout: '/assets/01-mascot/cutouts/peipei-skeptical-flat.png',
    frame: 'hero-frame-red',
  },
  {
    key: 'lanternSignal',
    image: '/assets/03-lore-scenes/04-lantern-signal-flat.png',
    cutout: '/assets/01-mascot/cutouts/peipei-lantern-flat.png',
    frame: 'hero-frame',
    glow: true,
  },
  {
    key: 'goldVault',
    image: '/assets/03-lore-scenes/05-gold-vault-flat.png',
    cutout: '/assets/01-mascot/cutouts/peipei-gold-bar-flat.png',
    frame: 'hero-frame-red',
  },
];

export const LoreChapters = () => {
  const { t } = useLocale();
  const reduced = useReducedMotion();

  return (
    <section id="story" data-testid="lore-section" className="relative py-20 sm:py-28 overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{ backgroundImage: 'url(/assets/04-backgrounds/peipei-eyes-pattern.svg)', backgroundSize: '420px' }}
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading testId="lore-heading" label={t.story.label} title={t.story.title} intro={t.story.intro} />

        <div className="mt-16 sm:mt-20 space-y-20 sm:space-y-28">
          {CHAPTERS.map((chapter, index) => {
            const copy = t.lore[chapter.key];
            const flipped = index % 2 === 1;
            return (
              <motion.article
                key={chapter.key}
                data-testid={`lore-chapter-${chapter.key}`}
                initial={reduced ? {} : { opacity: 0, y: 36 }}
                whileInView={reduced ? {} : { opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
                className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${flipped ? 'lg:[&>div:first-child]:order-2' : ''}`}
              >
                <div className="relative">
                  {chapter.glow && (
                    <motion.div
                      aria-hidden="true"
                      data-testid="lantern-glow-halo"
                      initial={reduced ? { opacity: 1 } : { opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true, margin: '-140px' }}
                      transition={{ duration: 1.4, ease: 'easeOut' }}
                      className="lantern-glow-halo absolute -inset-10 sm:-inset-16 pointer-events-none"
                    />
                  )}
                  <motion.div
                    initial={reduced || !chapter.glow ? {} : { opacity: 0 }}
                    whileInView={reduced || !chapter.glow ? {} : { opacity: 1 }}
                    viewport={{ once: true, margin: '-140px' }}
                    transition={{ duration: 1.2, ease: 'easeOut' }}
                    className={`${chapter.frame} ${chapter.glow ? 'lantern-glow' : ''} relative rounded-2xl overflow-hidden bg-card ${flipped ? 'rotate-[1.2deg]' : 'rotate-[-1.2deg]'}`}
                  >
                    <img src={chapter.image} alt={copy.alt} className="w-full h-auto block" loading="lazy" />
                  </motion.div>
                  <img
                    src={chapter.cutout}
                    alt=""
                    aria-hidden="true"
                    className={`absolute w-24 sm:w-28 drop-shadow-xl ${
                      flipped ? '-bottom-8 -right-4 sm:-right-8 rotate-[8deg]' : '-bottom-8 -left-4 sm:-left-8 rotate-[-8deg]'
                    }`}
                    loading="lazy"
                  />
                </div>

                <div className={flipped ? 'lg:pr-8' : 'lg:pl-8'}>
                  <p className="font-display text-sm font-bold uppercase tracking-[0.25em] text-primary">{copy.chapter}</p>
                  <h3 className="font-display mt-3 text-2xl sm:text-3xl font-bold text-foreground">{copy.title}</h3>
                  <p className="mt-5 text-base md:text-lg leading-relaxed text-muted-foreground max-w-lg">{copy.body}</p>
                  <div className="mt-6 h-1.5 w-20 rounded-full bg-accent" aria-hidden="true" />
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
